import { useMemo } from 'react';

import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { formatTimestampToGMTString } from '@/lib/utils/formatTimestampToGMTString';
import { getPoolIcon } from '@/lib/utils/getPoolIcon';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { T_UserPredictionEventParticipation } from '@/ts/types/T_UserBlockchainData';

export interface I_TransformedPredictionItem {
  participation: T_UserPredictionEventParticipation;
  Icon: React.ComponentType<{ className?: string }>;
  tokenIconUrl?: string;
  tokenIconAlt?: string;
  tokenSymbol?: string;
  showPredictionsLabel: boolean;
  predictedPrice?: string;
  dateTime: string;
  status: 'ongoing' | 'resolving' | 'won' | 'lost' | 'both' | 'canceled';
  wonAmountText?: string;
  lostAmountText?: string;
  refundAmountText?: string;
  isClaimWin: boolean;
  isClaimRefund: boolean;
  isClaimed: boolean;
  isClaimedRefund: boolean;
  showEyeIcon: boolean;
  tokenVariant: 'BTC' | 'ETH' | 'SOL' | 'XRP';
  predictionEvent: T_UserPredictionEventParticipation['predictionEvent'];
  isCurrentlyClaiming: boolean;
}

export function usePredictionHistoryTransformer(
  predictionHistoryData: T_UserPredictionEventParticipation[],
  claimingParticipationId?: string
): I_TransformedPredictionItem[] {
  const pools = useGlobalData('pools');

  return useMemo(() => {
    if (!predictionHistoryData.length) return [];

    return predictionHistoryData.map(participation => {
      const {
        predictionEvent,
        submissions,
        hasWon,
        hasClaimed,
        wonAmountDecimal,
        lostAmountDecimal,
        totalAmountDecimal,
        lastSubmissionTime
      } = participation;
      const { tokenIcon, tokenSymbol, resolved, resolutionTime, canceled } =
        predictionEvent;
      // Check if event is canceled
      const isCanceled = predictionEvent.canceled || false;

      const showEyeIcon =
        !isCanceled && // Don't show eye icon for canceled predictions
        (submissions.length > 1 ||
          (submissions.length === 1 &&
            submissions[0]?.predictedPricesDecimal?.length > 1));

      // For canceled status, don't show predictions label (show only tokenSymbol)
      const showPredictionsLabel = isCanceled ? false : showEyeIcon;

      // Find the pool associated with this prediction event to get decimal precision
      // We use predictionContract.contractAddress to match with pool.predictionAddress
      // Default to 2 decimals if pool not found
      const predictionDecimals =
        pools?.find(
          p =>
            p.predictionAddress?.toLowerCase() ===
            predictionEvent.predictionContract.contractAddress.toLowerCase()
        )?.decimals || 2;

      const predictedPrice =
        !showPredictionsLabel && submissions.length > 0
          ? formatNumberWithCommasAndDecimals(
              submissions[0]?.predictedPricesDecimal?.[0] || '0',
              predictionDecimals
            )
          : undefined;

      const dateTime = formatTimestampToGMTString(lastSubmissionTime);

      // Check if resolution time has passed
      const isResolutionTimePassed = resolutionTime
        ? new Date().getTime() >= parseInt(resolutionTime, 10) * 1000
        : false;

      let status:
        | 'ongoing'
        | 'resolving'
        | 'won'
        | 'lost'
        | 'both'
        | 'canceled';
      if (isCanceled) {
        status = 'canceled';
      } else if (!resolved) {
        if (isResolutionTimePassed) {
          status = 'resolving';
        } else {
          status = 'ongoing';
        }
      } else if (hasWon) {
        const lostAmount = lostAmountDecimal
          ? parseFloat(lostAmountDecimal)
          : 0;
        const hasLostAmount = lostAmount > 0;

        if (hasLostAmount) {
          status = 'both';
        } else {
          status = 'won';
        }
      } else {
        status = 'lost';
      }

      let wonAmountText: string | undefined;
      let lostAmountText: string | undefined;
      let refundAmountText: string | undefined;

      if ((status === 'won' || status === 'both') && wonAmountDecimal) {
        wonAmountText = `+ ${formatNumberWithCommasAndDecimals(wonAmountDecimal, 2)} USDC`;
      }

      if (status === 'lost' || status === 'both') {
        let missedAmount: string;

        if (lostAmountDecimal) {
          missedAmount = lostAmountDecimal;
        } else if (hasWon) {
          const total = parseFloat(totalAmountDecimal);
          missedAmount = Math.max(0, total - 1).toString();
        } else {
          missedAmount = totalAmountDecimal;
        }

        lostAmountText = `- ${formatNumberWithCommasAndDecimals(missedAmount, 2)} USDC`;
      }

      // For canceled status, refund amount and claim status will be determined by contract call
      // Don't set refundAmountText here - it will be fetched from contract
      // refundAmountText remains undefined for canceled predictions

      const isClaimWin = hasWon && !hasClaimed && resolved && !canceled;
      // For canceled status, claim refund availability will be determined by contract call
      // Set initial value, but it will be overridden by contract check
      const isClaimRefund = canceled ? true : false;
      const isClaimed = hasWon && hasClaimed && resolved && !canceled;
      // For canceled status, claimed status will be determined by contract call
      const isClaimedRefund = canceled ? false : false;
      const isCurrentlyClaiming = claimingParticipationId === participation.id;

      const tokenVariant =
        (tokenSymbol?.toUpperCase() as 'BTC' | 'ETH' | 'SOL' | 'XRP') || 'BTC';

      const Icon = getPoolIcon(tokenSymbol || 'BTC');

      return {
        participation,
        Icon,
        tokenIconUrl: tokenIcon?.url,
        tokenIconAlt: tokenIcon?.alt,
        tokenSymbol,
        showPredictionsLabel,
        predictedPrice,
        dateTime,
        status,
        wonAmountText,
        lostAmountText,
        refundAmountText,
        isClaimWin,
        isClaimRefund,
        isClaimed,
        isClaimedRefund,
        showEyeIcon,
        tokenVariant,
        predictionEvent,
        isCurrentlyClaiming
      };
    });
  }, [predictionHistoryData, claimingParticipationId, pools]);
}
